import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import useAssignVehicleToDriver from '../../API/hooks/vehicles/useAssignVehicleToDriver';
import useGetVehicleById from '../../API/hooks/vehicles/useGetVehicleById';
import useGetVehicleScoreDay from '../../API/hooks/vehicles/useGetVehicleScoreDay';
import useGetVehicleScoreYears from '../../API/hooks/vehicles/useGetVehicleScoreYears';
// import useGetDeviceLocations from '../../API/hooks/maps/useGetDeviceLocations';
import useGetVehicleLocations from '../../API/hooks/maps/useGetVehicleLocations';
import useDeleteVehicle from '../../API/hooks/vehicles/useDeleteVehicle';
import useVehicleThumbnail from '../../theme/useVehicleThumbnail';
import useDriverThumbnail from '../../theme/useDriverThumbnail';
import Icon from '../../components/common/Icon/Icon';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import KnobChartWidget from '../../components/common/KnobChartWidget/KnobChartWidget';
import DetailsPageSkeleton from '../../components/common/Skeleton/DetailsPageSkeleton';
import Tags from '../../components/common/Tags';
import HiddenDetails, { HiddenDetailTagItem } from '../../components/common/Tags/HiddenDetails/HiddenDetails';
import Modal from '../../components/common/Modal/Modal';
import WatchVideo from '../../components/common/LiveVideo/LiveVideo';
import TimeSelection from '../../components/common/Toolbar/TimeSelection';
import ScoreTrendWidget from '../../components/common/ScoreTrend/ScoreTrend';
import MapWidget from '../../components/common/Map/Map';
import { Button } from '../../components/ui/Button/Button';
import HeaderWrapper from '../../components/ui/DetailsPageHeaderWrapper/HeaderWrapper';
import { convertMsToKm, convertMsecToHs, generateDistance } from '../../utils/unitsUtils';
import S from './VehicleDetailsStyles';
import { ThemeType } from '../../theme/themeType';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetRole from '../../API/hooks/useGetRole';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import DetailsWidget from '../../components/common/DetailsWidget/DetailsWidget';
import EditVehicle from './Modals/EditVehicle/EditVehicle';
import {
  getEndOfTheDay, getStartOfTheDay, getTimeRangeText, getStartOfMonth, getPeriodDisplayLabel,
  isSingleDayPeriod,
} from '../../utils/dateUtils';
import { HourlySnapshot, YearlyDaySnapshot } from '../../API/types/Vehicles';
import CreateNote from '../../components/Notes/CreateNote/CreateNote';
import { logger } from '../../API/services/SharedService';
import useGetAlertsAggregation from '../../API/hooks/alerts/useGetAlertsAggregation';
import { getFilterFields, getSimpleEqQuery, queryFilters } from '../../utils/queryFilters';
import { generateVehicleTypeName, generateVehicleTypeIcon } from './helpers';
import {
  changeObjectKeysWithPrefix, createCSV, downloadFile, generateEventsLabel, generateScoreColor, generateVehicleStatusColor, getScoreLabel,
} from '../../utils/commonUtils';
import useGetFleetFeatures from '../../API/hooks/useGetFleetFeatures';
import {
  AlertRisksDognutConfig, AlertRisksKeys, AlertTypesDognutConfig, AlertTypesKeys,
} from '../../API/types/Alerts';
import useGetAlertRisks from '../../API/hooks/alerts/useGetAlertsRisks';
import DoghnutChartWidget from '../../components/common/DoghnutChartWidget/DoghnutChartWidget';
import useGetAlerts from '../../API/hooks/alerts/useGetAlerts';
import useGetTripsAverageSpeedByVehicleId from '../../API/hooks/trips/useGetTripsAverageSpeedByVehicleId';
import useGetTripsDurationByVehicleId from '../../API/hooks/trips/useGetTripsDurationByVehicleId';
import useGetAlertTypes from '../../API/hooks/alerts/useGetAlertsTypes';
import { getLocalStorageKey } from '../../utils/storageUtils';
import { FeaturesManagement } from '../../featuresControl';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetDeviceLiveVideoIsEnabled from '../../API/hooks/devices/useGetDeviceLiveVideoIsEnabled';
import useGetVPNEnabled from '../../API/hooks/devices/useGetVPNEnable';
import GroupsSetting from '../../components/GroupsSetting/GroupsSetting';
import useGetDriverById from '../../API/hooks/drivers/useGetDriverById';
import AuthorizedDrivers from '../../components/AuthorizedDrivers/AuthorizedDrivers';
import { vehicleDetailsPageFilters } from '../../constants/FilterDefault';
import { ENTITY_DETAILS_MAP_ALERTS_LIMIT } from '../../constants/EntityDetailsAlerts';
import useLocalStorage from '../../API/hooks/useLocalStorage';
import { VehicleStatus } from '../../constants/VehicleStatus';
import useGetActiveTripById from '../../API/hooks/trips/useGetActiveTripById';
import useGetTrips from '../../API/hooks/trips/useGetTrips';
import ActiveTripTags from '../../components/common/ActiveTripTags/ActiveTripTags';
import Annoucement from './AnnounceMent';

function VehicleDetailsPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();

  const alertRisksChartConfig: AlertRisksDognutConfig = {
    cards: {
      urgent: {
        color: theme.charts.alertRiskUrgent,
        label: 'Urgent',
      },
      medium: {
        color: theme.charts.alertRiskMedium,
        label: 'Medium',
      },
      low: {
        color: theme.charts.alertRiskLow,
        label: 'Low',
      },
    },
    title: 'Alert risks',
  };

  const alertTypesChartConfig: AlertTypesDognutConfig = {
    cards: {
      IMU: {
        color: theme.charts.alertImu,
        label: 'IMU',
      },
      frontCamera: {
        color: theme.charts.alertFrontCamera,
        label: 'Front cam',
      },
      inwardCamera: {
        color: theme.charts.alertInwardCamera,
        label: 'Inw cam',
      },
      cloud: {
        color: theme.charts.alertCloud,
        label: 'Cloud',
      },
    },
    title: 'Alert types',
  };

  const fleetId = useGetFleetId();
  const { isFleetViewer } = useGetRole();
  const { liveVideo: liveVideoEnabled } = useGetFleetFeatures();
  const navigate = useNavigate();
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('vehicles-page-details'), vehicleDetailsPageFilters);
  const { id: vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab');
  const isWideMapTab = !activeTab || activeTab === 'calendar' || activeTab === 'driver' || activeTab === 'alert';
  const { data: fleetData } = useGetFleet(fleetId);
  const { data: vehicle, isLoading, error } = useGetVehicleById(vehicleId as string, fleetId);
  const { data: driver } = useGetDriverById(vehicle?.driver?._id || '', fleetId);
  const { data: vehicleEvents } = useGetAlertsAggregation(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('vehicleId', vehicleId, 0)}`);
  const activeFilters = filters || vehicleDetailsPageFilters;
  const filterPeriod = activeFilters.period?.[0];
  const filterStartDate = activeFilters.startDate?.[0];
  const filterEndDate = activeFilters.endDate?.[0];

  // Determine score fetch mode based on active filter
  const isDayMode = filterPeriod === 'daily';
  const isCustomSingleDay = filterPeriod === 'custom' && filterStartDate && filterEndDate
    && new Date(filterStartDate).toDateString() === new Date(filterEndDate).toDateString();
  const useHourlyMode = isDayMode || isCustomSingleDay;

  const toLocalDateString = (iso: string): string => {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const scoreDayDateResolved = filterStartDate
    ? toLocalDateString(filterStartDate)
    : toLocalDateString(new Date().toISOString());
  const scoreDayDate = useHourlyMode ? scoreDayDateResolved : '';

  const scoreDayDatePrev = useHourlyMode && scoreDayDate
    ? (() => { const d = new Date(`${scoreDayDate}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); })()
    : '';

  const scoreDayDateNext = useHourlyMode && scoreDayDate
    ? (() => { const d = new Date(`${scoreDayDate}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); })()
    : '';

  const currentYear = new Date().getFullYear();
  const { data: vehicleScoreDay } = useGetVehicleScoreDay(
    vehicleId as string,
    fleetId,
    scoreDayDate,
  );

  const { data: vehicleScoreDayPrev } = useGetVehicleScoreDay(
    vehicleId as string,
    fleetId,
    scoreDayDatePrev,
  );

  const { data: vehicleScoreDayNext } = useGetVehicleScoreDay(
    vehicleId as string,
    fleetId,
    scoreDayDateNext,
  );

  const { data: vehicleScoreYears } = useGetVehicleScoreYears(
    vehicleId as string,
    fleetId,
    2020,
    currentYear,
  );

  // Build score data for trend chart and average based on active filter
  const allDays: YearlyDaySnapshot[] = vehicleScoreYears?.years?.flatMap((y) => y.days) ?? [];

  const getFilteredDays = (): YearlyDaySnapshot[] => {
    if (useHourlyMode) return [];
    if (filterPeriod === 'custom' && filterStartDate && filterEndDate) {
      const start = new Date(filterStartDate).getTime();
      const end = new Date(filterEndDate).getTime();
      return allDays.filter((d) => {
        const t = new Date(d.date).getTime();
        return t >= start && t <= end;
      });
    }
    if (filterPeriod === 'daily') {
      const today = new Date().toDateString();
      return allDays.filter((d) => new Date(d.date).toDateString() === today);
    }
    if (filterPeriod === 'weekly') {
      const weekStart = new Date(filterStartDate || '').getTime();
      const now = Date.now();
      return allDays.filter((d) => {
        const t = new Date(d.date).getTime();
        return t >= weekStart && t <= now;
      });
    }
    if (filterPeriod === 'monthly') {
      const monthStart = new Date(filterStartDate || getStartOfMonth().toISOString()).getTime();
      const now = Date.now();
      return allDays.filter((d) => {
        const t = new Date(d.date).getTime();
        return t >= monthStart && t <= now;
      });
    }
    // All
    return allDays;
  };

  const filteredDays = getFilteredDays();

  const buildHourlyTrendData = (): { scoreDate: string; score: number }[] => {
    if (!useHourlyMode || !scoreDayDate) return [];

    const snapshotsToPoints = (dayDate: string, snapshots: HourlySnapshot[]): { scoreDate: string; score: number; ts: number }[] => snapshots.map((s) => {
      const base = new Date(`${dayDate}T00:00:00Z`);
      base.setUTCHours(s.hour, s.minute ?? 0, 0, 0);
      return { scoreDate: base.toISOString(), score: s.score, ts: base.getTime() };
    });

    const allPoints = [
      ...(vehicleScoreDay?.hourlySnapshots?.length ? snapshotsToPoints(scoreDayDate, vehicleScoreDay.hourlySnapshots) : []),
    ]
      .sort((a, b) => a.ts - b.ts)
      .map(({ scoreDate, score }) => ({ scoreDate, score }));

    if (allPoints.length) return allPoints;

    if (vehicleScoreDay?.score != null) {
      return [{ scoreDate: new Date(scoreDayDate).toISOString(), score: vehicleScoreDay.score }];
    }
    return [];
  };

  const trendData: { scoreDate: string; score: number }[] = useHourlyMode
    ? buildHourlyTrendData()
    : filteredDays.map((d) => ({ scoreDate: d.date, score: d.score }));

  const weightedAvgScore = (days: YearlyDaySnapshot[]): number | null => {
    const totalDrivingTime = days.reduce((sum, d) => sum + (d.drivingTimeMs ?? 0), 0);
    if (totalDrivingTime <= 0) return null;
    const weightedSum = days.reduce((sum, d) => sum + d.score * (d.drivingTimeMs ?? 0), 0);
    return weightedSum / totalDrivingTime;
  };

  const averageScoreFromDays = weightedAvgScore(filteredDays);
  const averageScore = useHourlyMode ? (vehicleScoreDay?.score ?? null) : averageScoreFromDays;

  const allTimeAverage = weightedAvgScore(allDays);

  const { data: risksChartData } = useGetAlertRisks(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('vehicleId', vehicleId, 0)}${getFilterFields(filters || vehicleDetailsPageFilters, ['period'], 'eventDate', 1)}`);
  const { data: typesChartData } = useGetAlertTypes(fleetId, `?limit=1000&skip=0${getSimpleEqQuery('vehicleId', vehicleId, 0)}${getFilterFields(filters || vehicleDetailsPageFilters, ['period'], 'eventDate', 1)}`);
  const { data: averageSpeed } = useGetTripsAverageSpeedByVehicleId(fleetId, vehicleId as string);
  const { data: tripsDuration } = useGetTripsDurationByVehicleId(fleetId, vehicleId as string);
  const tripTabFilters = { sortBy: ['startAt_DESC'], ...activeFilters };
  const tripsTotalQuery = queryFilters(
    tripTabFilters,
    1,
    getSimpleEqQuery('vehicleId', vehicleId, 9),
    'startAt',
    1,
  );
  const { data: tripsTotalData } = useGetTrips(fleetId, tripsTotalQuery);
  const tripsTotal = tripsTotalData?.total ?? 0;
  const videoStreamEnable = useGetDeviceLiveVideoIsEnabled(vehicle?.device?.deviceId, fleetId);
  const VPNStatus = useGetVPNEnabled(vehicle?.device?.deviceId, fleetId);
  const { data: activeTrips } = useGetTrips(fleetId, `?limit=10&skip=0&sortField=createdAt&sortOrder=DESC${getSimpleEqQuery('vehicleId', vehicleId, 0)}${getSimpleEqQuery('endAt', 'null', 1)}`);
  const activeTrip = activeTrips?.results?.[0];
  const { data: activeTripData } = useGetActiveTripById(fleetId, activeTrip);

  const { data: alerts, isSuccess: alertsLoaded } = useGetAlerts(
    fleetId,
    queryFilters(
      activeFilters,
      undefined,
      getSimpleEqQuery('vehicleId', vehicleId as string, 20),
      'eventDate',
      ENTITY_DETAILS_MAP_ALERTS_LIMIT,
    ),
    !!vehicleId,
  );

  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [watchCameraModalOpen, toggleWatchVideoModal] = useState(false);
  const [editModalOpen, toggleEditModalOpen] = useState(false);
  const [annouceementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [addNoteModalOpen, toggleAddNoteModalOpen] = useState(false);
  const [groupsModalOpen, toggleGroupsModalOpen] = useState(false);
  const [authorizedDriversModalOpen, toggleAuthorizedDriversModalOpen] = useState(false);
  const handleBackToList = (): void => navigate('/vehicles/');
  const handleWatchVideo = (): void => toggleWatchVideoModal(true);

  const onSubmitFail = (err: string): void => {
    logger.error('VehicleDetails', 'On submit fail', err);
  };

  const handleGoBack = (): void => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      handleBackToList();
    }
  };

  const handleDeleteClick = (): void => {
    toggleDeleteModalOpen(true);
  };

  const { mutate: mutateDelete, isLoading: isDeleteLoading } = useDeleteVehicle(
    handleBackToList,
    onSubmitFail,
  );

  const handleDeleteVehicle = (): void => mutateDelete({
    fleetId,
    vehicleId: vehicleId as string,
  });

  const handleEditVehicle = (): void => {
    toggleEditModalOpen(true);
  };

  const handleSetAuthorizedDrivers = (): void => {
    toggleAuthorizedDriversModalOpen(true);
  };

  const handleAnnouncementVehicle = (): void => {
    setAnnouncementModalOpen(true);
  };

  Object.keys(alertRisksChartConfig.cards).forEach((key): void => {
    alertRisksChartConfig.cards[key as AlertRisksKeys]!.count = risksChartData?.[key as AlertRisksKeys] || 0;
  });

  Object.keys(alertTypesChartConfig.cards).forEach((key): void => {
    alertTypesChartConfig.cards[key as AlertTypesKeys]!.count = typesChartData?.[key as AlertTypesKeys] || 0;
  });

  const periodLabel = getPeriodDisplayLabel(activeFilters);
  const scoreLabel = isSingleDayPeriod(activeFilters) ? 'Daily score' : 'Average score';
  alertRisksChartConfig.title = `Alert risks • ${periodLabel}`;
  alertTypesChartConfig.title = `Alert types • ${periodLabel}`;

  // const handleAddNote = (): void => {
  //   toggleAddNoteModalOpen(true);
  // };

  if (error) {
    handleBackToList();
  }
  const reportName = `${fleetData?.name || 'fleet_na'}_vehicles_${getTimeRangeText(filters || vehicleDetailsPageFilters)}_widgets`;
  const hiddenVehicleTagItems: HiddenDetailTagItem[] = [{
    key: 'vehicle-details-more-tags',
    node: (
      <>
        <Tags.LabeledIcon
          flexible
          label={generateDistance(vehicle?.distanceInKmTotal, true)}
          icon={IconKeys.DISTANCE}
        />
        {!!tripsDuration && tripsDuration.durationInMs !== null && (
          <Tags.Label
            label={tripsDuration.durationInMs > 0 ? `Driving time: ${convertMsecToHs(tripsDuration.durationInMs)} hours in total` : 'Driving time: N/A'}
          />
        )}
        {!!averageSpeed?.averageSpeedMs && (
          <Tags.Label
            label={`Average speed: ${(averageSpeed && averageSpeed.averageSpeedMs !== null) ? convertMsToKm(averageSpeed.averageSpeedMs) : '0'} km/h in total`}
          />
        )}
        {tripsTotal != null && (
          <Tags.Label
            label={`${tripsTotal} trips in total`}
          />
        )}
        {vehicleEvents?.count != null && (
          <Tags.LabeledIcon
            flexible
            label={generateEventsLabel(vehicleEvents.count)}
            icon={IconKeys.ALERT_CIRCLE}
          />
        )}
        <Tags.LabeledIcon
          label={vehicle?.category ? generateVehicleTypeName(vehicle.category) : 'N/A'}
          icon={generateVehicleTypeIcon(vehicle?.category)}
          flexible
        />
      </>
    ),
  }];
  console.log(driver, 'fleet');
  return (
    <>
      {isLoading && (<DetailsPageSkeleton />)}
      {!!vehicle && (
        <S.Wrapper>
          <HeaderWrapper>
            <div className="details-row">
              <div className="overview-container">
                <div className="preview-container">
                  <Button height="44px" padding="10px 12px" onClick={handleGoBack}>
                    <Icon iconKey={IconKeys.ARROW_LEFT} />
                  </Button>
                  <Tags.ItemOverview
                    avatar={vehicleThumbnail}
                    avatarAlt="vehicle"
                    title={vehicle.licensePlateNumber}
                    subtitle={`${vehicle.manufacturer ?? ''} ${vehicle.model ?? ''}`}
                  />
                </div>
                {!!vehicle?.driver && (
                  <Tags.WithUnassign
                    link={`/drivers/${vehicle.driver._id}`}
                    imageSrc={driver?.avatarUrl || driverThumbnail}
                    title={`${vehicle.driver.firstName} ${vehicle.driver.lastName}`}
                    description={vehicle.driver?.licenseName || 'No license added'}
                    apiCall={useAssignVehicleToDriver}
                    mutationConfig={{ driverId: null, fleetId, vehicleId: vehicle._id }}
                  />
                )}
                {!!vehicle.device?.deviceId && (
                  <Tags.DeviceInfo
                    title={vehicle.device.deviceId}
                    description="Connected device"
                    link={`/devices/${vehicle.device._id}`}
                  />
                )}
              </div>
              <div className="actions-wrapper">
                {/* <div className="border-line" />
                {vehicle.isDriverObstruction !== undefined && vehicle.isDriverObstruction !== null && driver && vehicle.device && (
                  <div className={`obstruction-status ${!vehicle.isDriverObstruction ? 'driver-detected' : 'driver-not-detected'}`}>
                    <p>Driver is</p>
                    <p>{!vehicle.isDriverObstruction ? 'detected' : 'NOT detected'}</p>
                  </div>
                )} */}
                <Button
                  height="44px"
                  padding="10px 12px"
                  title={liveVideoEnabled && videoStreamEnable ? 'Video stream' : 'Video streaming is disabled.\nPlease contact your administractor to turn on.'}
                  disabled={!vehicle?.device?.isOnline || !liveVideoEnabled || !videoStreamEnable}
                  onClick={handleWatchVideo}
                >
                  <Icon iconKey={IconKeys.VIDEO} />
                </Button>
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="announcement"
                    onClick={handleAnnouncementVehicle}
                  >
                    <Icon iconKey={IconKeys.ANNOUNCEMENT} />
                  </Button>
                )}

                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Edit"
                    onClick={handleEditVehicle}
                  >
                    <Icon iconKey={IconKeys.EDIT} />
                  </Button>
                )}
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Authorized Drivers"
                    onClick={handleSetAuthorizedDrivers}
                  >
                    <Icon iconKey={IconKeys.USERS} />
                  </Button>
                )}
                {!isFleetViewer && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title="Delete"
                    disabled={isDeleteLoading}
                    onClick={handleDeleteClick}
                  >
                    <Icon iconKey={IconKeys.DELETE} />
                  </Button>
                )}
                <Button
                  height="44px"
                  padding="10px 12px"
                  title="Create report"
                  disabled={isDeleteLoading}
                  onClick={() => {
                    downloadFile(createCSV([{
                      ...vehicle as Record<string, any>,
                      ...changeObjectKeysWithPrefix({ ...risksChartData, ...typesChartData }, 'alerts_'),
                    }]), reportName, 'text/csv');
                  }}
                >
                  <Icon iconKey={IconKeys.SUMMARIZE} />
                </Button>
              </div>
              <div className="actions-container" />
            </div>
            <div className="sub-header">
              <div className="tags-row">
                {allTimeAverage !== null ? (<Tags.Status label={`${Math.round(allTimeAverage)} score in total`} backgroundColor={generateScoreColor(allTimeAverage)} />) : (<Tags.Status label="No score" backgroundColor={theme.colors.outline} />)}
                <Tags.Status
                  label={vehicle.status ? VehicleStatus[vehicle.status] : 'No driving status'}
                  backgroundColor={generateVehicleStatusColor(vehicle.status)}
                  title="Vehicle status"
                />
                {!isFleetViewer && FeaturesManagement.features.groups && (
                  <Tags.LabeledIcon
                    label={!vehicle.groups?.length ? 'Add to group' : `Groups count: ${vehicle.groups.length}`}
                    icon={IconKeys.LIST}
                    onClick={() => toggleGroupsModalOpen(true)}
                    flexible
                  />
                )}
                {activeTrip && (
                  <ActiveTripTags
                    startAt={activeTrip.startAt}
                    deviceId={activeTrip.deviceId || vehicle?.device?.deviceId}
                    speedMs={activeTripData?.speedMs}
                    distanceInKm={activeTripData?.distanceInKm}
                    onClick={() => navigate(
                      `/trips/${(activeTrip.isMergedTrip && activeTrip.mergedTripId) ? activeTrip.mergedTripId : (activeTrip._id || '')}`,
                      { state: { isMergedTrip: !!activeTrip.isMergedTrip } },
                    )}
                  />
                )}
                <HiddenDetails items={hiddenVehicleTagItems} label="More details..." />
              </div>
              <TimeSelection allTimeOption filters={filters} setFilters={setFilters} />
            </div>
          </HeaderWrapper>
          <div className="widgets-container">
            <DetailsWidget
              fleetId={fleetId}
              entity="vehicle"
              entityId={vehicleId as string}
              filters={filters}
              showTabs={{
                calendar: true,
                alert: true,
                driver: true,
                trip: true,
                video: true,
                note: true,
                geofence: FeaturesManagement.features.geofence,
              }}
            />
            {/* TODO: implement trips isntead of current position */}
            {/* <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetDeviceLocations}
              entityId={tripData?.deviceId as string}
              entityType="vehicle"
              startDate={tripData?.startAt}
              endDate={tripData?.endAt || getEndOfTheDay().toISOString()}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
              isTrip
              hasTripVehicleMarker
            /> */}
            <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetVehicleLocations}
              entityId={vehicleId as string}
              entityType="vehicle"
              hasActiveTripForSpeed={!!activeTrip}
              showDetailsButton={false}
              routeHistoryFilters={activeFilters}
              mapWrapperWidth={isWideMapTab ? '50%' : '403px'}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
            />
          </div>
          <S.Row>
            <ScoreTrendWidget
              title={`Vehicle score history • ${periodLabel}`}
              period={useHourlyMode ? 'daily' : (activeFilters.period?.[0] ?? 'monthly')}
              data={trendData}
            />
            <div className="vehicle-score">
              <KnobChartWidget
                value={averageScore !== null ? Math.round(averageScore) : 'N/A'}
                max={100}
                subtitle={`${scoreLabel} • ${periodLabel}`}
              />
            </div>
          </S.Row>
          {/* <TimelineWidget data={data} /> */}
          <S.Row>
            <S.Widget>
              <DoghnutChartWidget data={alertRisksChartConfig} />
            </S.Widget>
            <S.Widget>
              <DoghnutChartWidget data={alertTypesChartConfig} type="quad" />
            </S.Widget>

          </S.Row>
        </S.Wrapper>
      )}
      {watchCameraModalOpen && (
        <Modal
          title="Video stream"
          state={watchCameraModalOpen}
          toggleState={toggleWatchVideoModal}
        >
          <WatchVideo
            // fleetId={fleetId}
            deviceId={vehicle?.device?.deviceId as string}
            VPNStatus={VPNStatus}
            deviceStatus={vehicle?.device?.isOnline}
          // toggleModal={toggleWatchVideoModal}
          />
        </Modal>
      )}
      {editModalOpen && (
        <Modal
          title="Edit vehicle details"
          state={editModalOpen}
          toggleState={toggleEditModalOpen}
        >
          <EditVehicle fleetId={fleetId} vehicle={vehicle} toggleModal={toggleEditModalOpen} />
        </Modal>
      )}
      {annouceementModalOpen && (
        <Modal
          title="Assign Announcement to vehicle"
          state={annouceementModalOpen}
          toggleState={setAnnouncementModalOpen}
          settings={{
            minWidth: '900px',
            minHeight: '600px',
          }}
        >
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
          >
            <Annoucement
              fleetId={driver?.device?.fleetId}
              deviceId={driver?.device?.deviceId}
              toggleModal={setAnnouncementModalOpen}
            />
          </div>
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this vehicle?"
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={handleDeleteVehicle}
          />
        </Modal>
      )}
      {addNoteModalOpen && vehicleId && (
        <Modal
          title="Add note for vehicle"
          state={addNoteModalOpen}
          toggleState={toggleAddNoteModalOpen}
        >
          <CreateNote fleetId={fleetId} entityName="vehicles" entityId={vehicleId} toggleModal={toggleAddNoteModalOpen} />
        </Modal>
      )}
      {groupsModalOpen && !isFleetViewer && vehicle?._id && (
        <Modal
          title="Manage groups for vehicle"
          state={groupsModalOpen}
          toggleState={toggleGroupsModalOpen}
        >
          {/* <CreateNote fleetId={fleetId} entityName="vehicles" entityId={vehicleId} toggleModal={toggleAddNoteModalOpen} /> */}
          <GroupsSetting
            entity="vehicle"
            entityId={vehicle._id}
            fleetId={fleetId}
            groups={vehicle?.groups}
            onClose={() => toggleGroupsModalOpen(false)}
          />
        </Modal>
      )}
      {authorizedDriversModalOpen && !isFleetViewer && vehicle?._id && (
        <Modal
          title="Manage authorized drivers for vehicle"
          state={authorizedDriversModalOpen}
          toggleState={toggleAuthorizedDriversModalOpen}
        >
          <AuthorizedDrivers
            vehicleId={vehicle._id}
            fleetId={fleetId}
            onClose={() => toggleAuthorizedDriversModalOpen(false)}
          />
        </Modal>
      )}
    </>
  );
}

export default VehicleDetailsPage;
